/**
 * EuHistoricalViewerEmbed — thin wrapper around `RegulatoryViewerEmbed` bound to
 * the archived-EU-historical single-version API client (`euHistoryApi`).
 *
 * `docId` is an `eu_historical_documents.id` (the stable registry document id).
 * All page/text-span/TOC data is served on demand from the SOR PDF by the
 * `/api/eu-history` adapter — the viewer itself is byte-identical to every other
 * family's. See EUViewerEmbed.tsx for the shared rationale.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata } from '../../types/viewer';
import { euHistoryApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
  type SectionSpan,
} from './RegulatoryViewerEmbed';

export type EuHistoricalViewerHandle = RegulatoryViewerHandle;

interface EuHistoricalViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  highlightColors?: (string | undefined)[] | null;
  /** Deep-link landing: preferred section number (via TOC), plus a page (1-based)
   *  + normalized Y (0..1) fallback. Forwarded to RegulatoryViewerEmbed, whose
   *  race-safe effect lands once pages + TOC mount (US-SBA chunk parity). */
  landingSection?: string | undefined;
  landingPage?: number | undefined;
  landingY?: number | undefined;
  /** Deterministic per-page section boxes to highlight (normalized 0..1). */
  sectionSpans?: SectionSpan[] | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
}

const EuHistoricalViewerEmbed = forwardRef<
  EuHistoricalViewerHandle,
  EuHistoricalViewerEmbedProps
>(function EuHistoricalViewerEmbed(props, ref) {
  const innerRef = useRef<RegulatoryViewerHandle>(null);
  useImperativeHandle(ref, () => ({
    scrollToPage: (n) => innerRef.current?.scrollToPage(n),
    highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
    scrollToSection: (s) => innerRef.current?.scrollToSection(s) ?? false,
  }));
  return (
    <RegulatoryViewerEmbed
      ref={innerRef}
      viewerApi={euHistoryApi}
      {...props}
    />
  );
});

export default EuHistoricalViewerEmbed;
